/*
  Neo Noir — assets/neo-noir.js
  Self-contained custom element. No dependencies, no globals.
  Safe with Theme Editor section reloads (element re-connects cleanly).
*/
(() => {
  'use strict';

  if (!window.customElements || window.customElements.get('neo-noir-campaign')) return;

  const SALT = 'neo-noir:';
  const VERIFY_DELAY_MS = 450;
  const BADGES = { open: 'Open', locked: 'Locked', solved: 'Decrypted', soon: 'Pending' };

  // Must mirror the Liquid normalisation in sections/neo-noir.liquid:
  // lowercase, then keep only a–z and 0–9.
  const normalize = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');

  const sha256 = async (text) => {
    const data = new TextEncoder().encode(text);
    const buffer = await window.crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(buffer), (b) => b.toString(16).padStart(2, '0')).join('');
  };

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const getStorage = (mode) => {
    try {
      const area =
        mode === 'local' ? window.localStorage : mode === 'session' ? window.sessionStorage : null;
      if (!area) return null;
      const probe = '__neo_noir_probe__';
      area.setItem(probe, '1');
      area.removeItem(probe);
      return area;
    } catch (error) {
      return null; // private mode / blocked storage: progress simply won't persist
    }
  };

  const restartClass = (el, className) => {
    el.classList.remove(className);
    void el.offsetWidth; // force reflow so the animation replays
    el.classList.add(className);
  };

  class NeoNoirCampaign extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;

      this.storageKey = `neo-noir:${this.dataset.campaignKey || 'default'}`;
      this.storage = getStorage(this.dataset.progress);
      this.sequential = this.dataset.sequential === 'true';
      this.preview = this.dataset.previewSolved === 'true';
      this.emptyMessage = this.dataset.emptyMessage || 'Enter a password to continue.';
      this.phases = Array.from(this.querySelectorAll('[data-nn-phase]'));
      this.solved = new Set(
        this.preview ? this.phases.map((p) => p.dataset.blockId) : this.readProgress()
      );

      this.onSubmit = this.onSubmit.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onStorage = this.onStorage.bind(this);

      this.addEventListener('submit', this.onSubmit);
      this.addEventListener('input', this.onInput);
      window.addEventListener('storage', this.onStorage);

      this.render(false);
      this.classList.add('is-ready');
    }

    disconnectedCallback() {
      this.removeEventListener('submit', this.onSubmit);
      this.removeEventListener('input', this.onInput);
      window.removeEventListener('storage', this.onStorage);
      this._connected = false;
    }

    /* ---------- progress ---------- */

    readProgress() {
      if (!this.storage) return [];
      try {
        const data = JSON.parse(this.storage.getItem(this.storageKey) || 'null');
        return data && Array.isArray(data.solved) ? data.solved : [];
      } catch (error) {
        return [];
      }
    }

    writeProgress() {
      if (!this.storage || this.preview) return;
      try {
        this.storage.setItem(this.storageKey, JSON.stringify({ v: 1, solved: Array.from(this.solved) }));
      } catch (error) {
        /* quota / blocked: ignore */
      }
    }

    onStorage(event) {
      // Another tab solved a phase (or storage was cleared).
      if (this.preview || event.storageArea !== this.storage) return;
      if (event.key !== null && event.key !== this.storageKey) return;
      this.solved = new Set(this.readProgress());
      this.render(false);
    }

    /* ---------- state ---------- */

    render(animate) {
      let gateOpen = true;
      this.phases.forEach((phase) => {
        let state;
        if (phase.dataset.status === 'soon') state = 'soon';
        else if (this.solved.has(phase.dataset.blockId)) state = 'solved';
        else if (!gateOpen) state = 'locked';
        else state = 'open';

        if (this.sequential && state !== 'solved') gateOpen = false;
        this.applyState(phase, state, animate);
      });
    }

    applyState(phase, state, animate) {
      const previous = phase.dataset.state;
      phase.dataset.state = state;

      const badge = phase.querySelector('[data-nn-badge]');
      if (badge) badge.textContent = BADGES[state] || '';
      if (state === 'soon') return;

      const locked = phase.querySelector('[data-nn-locked]');
      const riddle = phase.querySelector('[data-nn-riddle]');
      const form = phase.querySelector('[data-nn-form]');
      const fieldset = phase.querySelector('.neo-noir__fieldset');
      const reveal = phase.querySelector('[data-nn-reveal]');

      if (locked) locked.hidden = state !== 'locked';
      if (riddle) riddle.hidden = state === 'locked';
      if (form) form.hidden = state !== 'open';
      if (fieldset) fieldset.disabled = state !== 'open';

      if (!reveal) return;
      if (state === 'solved') {
        this.showReveal(phase, reveal, animate && previous !== 'solved');
      } else {
        reveal.hidden = true;
        reveal.replaceChildren();
      }
    }

    showReveal(phase, target, animate) {
      if (!target.childElementCount) {
        const template = phase.querySelector('template[data-nn-reveal-template]');
        if (template) target.appendChild(template.content.cloneNode(true));
      }
      target.hidden = false;
      if (animate) restartClass(phase, 'is-revealing');
    }

    /* ---------- interaction ---------- */

    onInput(event) {
      const input = event.target.closest && event.target.closest('.neo-noir__input');
      if (!input || input.getAttribute('aria-invalid') !== 'true') return;
      input.removeAttribute('aria-invalid');
      this.setMessage(input.closest('[data-nn-phase]'), '');
    }

    async onSubmit(event) {
      const form = event.target.closest && event.target.closest('[data-nn-form]');
      if (!form || !this.contains(form)) return;
      event.preventDefault();

      const phase = form.closest('[data-nn-phase]');
      // Guard against locked/solved phases even if disabled UI is bypassed.
      if (!phase || phase.dataset.state !== 'open' || phase.dataset.busy === 'true') return;

      const input = form.querySelector('.neo-noir__input');
      const button = form.querySelector('.neo-noir__button');
      const value = normalize(input.value);

      if (!value) {
        this.fail(phase, input, this.emptyMessage, false);
        return;
      }

      const answers = (phase.dataset.answers || '').split(',').filter(Boolean);
      phase.dataset.busy = 'true';
      button.setAttribute('aria-busy', 'true');

      let correct = false;
      try {
        const [hash] = await Promise.all([sha256(SALT + value), wait(VERIFY_DELAY_MS)]);
        correct = answers.includes(hash);
      } catch (error) {
        console.error('[Neo Noir] Password check failed:', error);
      } finally {
        delete phase.dataset.busy;
        button.removeAttribute('aria-busy');
      }

      if (correct) this.succeed(phase, input);
      else this.fail(phase, input, phase.dataset.errorMessage || 'Access denied', true);
    }

    fail(phase, input, message, shake) {
      this.setMessage(phase, message, 'error');
      input.setAttribute('aria-invalid', 'true');
      if (shake) restartClass(phase, 'is-error');
      input.focus();
      input.select();
    }

    succeed(phase, input) {
      input.removeAttribute('aria-invalid');
      input.value = '';
      this.solved.add(phase.dataset.blockId);
      this.writeProgress();
      this.render(true);
      this.setMessage(phase, phase.dataset.successMessage || 'Access granted', 'success');

      const focusTarget = phase.querySelector('[data-nn-focus]');
      if (focusTarget) focusTarget.focus();

      // Hook for analytics later. Never includes the answer.
      this.dispatchEvent(
        new CustomEvent('neo-noir:phase-solved', {
          bubbles: true,
          detail: { blockId: phase.dataset.blockId, index: this.phases.indexOf(phase) + 1 },
        })
      );
    }

    setMessage(phase, text, type) {
      const el = phase && phase.querySelector('[data-nn-message]');
      if (!el) return;
      el.textContent = text;
      el.dataset.type = type || '';
    }
  }

  window.customElements.define('neo-noir-campaign', NeoNoirCampaign);
})();